import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { useInvestorCrmOrgStore } from '../../store/investorCrmOrg.store';
import organizationsService from '../../services/crm/organizationsService';
import InvestorCrmHubHeader from './components/InvestorCrmHubHeader';
import InvestorCommandPalette from './components/InvestorCommandPalette';
import PipelineTab from './tabs/PipelineTab';
import InvestorsTab from './tabs/InvestorsTab';

type TabId = 'pipeline' | 'investors' | 'analytics';

interface Tab {
  id: TabId;
  label: string;
  icon: string;
  component: React.ComponentType;
}

const TABS: Tab[] = [
  { id: 'pipeline', label: 'Pipeline', icon: '📊', component: PipelineTab },
  { id: 'investors', label: 'Investors', icon: '📋', component: InvestorsTab }
];

const InvestorCrmHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrgId, isBootstrapped, setActiveOrgId, setBootstrapped, setCurrentUserId } = useInvestorCrmOrgStore();
  
  const [activeTab, setActiveTab] = useState<TabId>('investors');
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  useEffect(() => {
    setCurrentUserId(user?.id ?? null);
  }, [user?.id, setCurrentUserId]);

  // Bootstrap organizations on mount
  useEffect(() => {
    const bootstrapOrganizations = async () => {
      if (!user?.id) return;
      
      try {
        const response = await organizationsService.list();
        const list = response.data || [];

        if (list.length === 0) {
          const created = await organizationsService.create('My Organization');
          const createdOrg = created.data;
          if (createdOrg) {
            setActiveOrgId(createdOrg.id);
          }
        } else {
          if (!activeOrgId) {
            setActiveOrgId(list[0]?.id ?? null);
          }
        }
      } catch (err: any) {
        console.error('Failed to bootstrap organizations:', err);
        toast.error(err?.message || 'Failed to load organizations');
      } finally {
        setBootstrapped(true);
      }
    };

    void bootstrapOrganizations();
  }, [user?.id, activeOrgId, setActiveOrgId, setBootstrapped]);

  // Set document title
  useEffect(() => {
    document.title = 'Investor CRM | Explanatum';
  }, []);

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Parse active tab from URL hash
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && TABS.find(t => t.id === hash)) {
      setActiveTab(hash as TabId);
    }
  }, [location.hash]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
      }
      // Escape to close command palette
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
    navigate(`#${tabId}`, { replace: true });
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isBootstrapped) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="rounded-2xl bg-white p-8 shadow-sm ring-1 ring-gray-100">
          <div className="flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Initializing Investor CRM...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <InvestorCrmHubHeader />
        <div className="mt-6 rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-gray-100">
          <div className="mx-auto max-w-md">
            <div className="mb-4 text-4xl">💼</div>
            <h3 className="text-lg font-semibold text-gray-900">No Organization Selected</h3>
            <p className="mt-2 text-sm text-gray-600">
              Please create or select an organization to access the Investor CRM.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ActiveTabComponent = TABS.find(t => t.id === activeTab)?.component || InvestorsTab;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <InvestorCrmHubHeader />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6" aria-label="Tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition
                    ${activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <ActiveTabComponent />
          </div>
        </div>

        {/* Command Palette Hint */}
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowCommandPalette(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm transition hover:bg-gray-50"
          >
            <span>Press</span>
            <kbd className="rounded border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-semibold">⌘K</kbd>
            <span>for quick actions</span>
          </button>
        </div>
      </div>

      {/* Command Palette */}
      <InvestorCommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onNavigate={(tabId: string) => {
          handleTabChange(tabId as TabId);
          setShowCommandPalette(false);
        }}
      />
    </div>
  );
};

export default InvestorCrmHub;
